import ScopeChangeRequestStore, {
  FindScopeChangeRequest,
} from 'admin/ScopeChangeRequestStore'
import { TableSortOrder } from 'components/Table/ClickableTable'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import { SingleSelectFilter } from 'infra/FilterControls/SingleSelect/SingleSelectFilter'
import {
  FilterItem,
  FilterItemSource,
  FilterStorage,
  FiltersViewModel,
} from 'infra/FiltersViewModel'
import omit from 'lodash/omit'
import identity from 'lodash/identity'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { combineLatest, Observable, Subscription } from 'rxjs'
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators'
import { CursorPageQuery, Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { toStream, tapAction } from 'utils/mobx-util'
import { areDeepEqual } from 'utils/object-util'
import { match } from 'utils/pattern-match-util'
import { shareLatest, withCancellation } from 'utils/rx-util'
import ScopeChangeRequest, {
  ScopeChangeRequestStatus,
} from './ScopeChangeRequest'
import { SearchableScopeChangeRequestSortField } from './ScopeChangeRequestsTableColumn'
import uniq from 'lodash/uniq'
import { FetchJobs } from './ScopeChangeRequestsViewModel'
import ScopeChangeRequestQueryBuilder from './ScopeChangeRequestQueryStringBuilder'
import {
  GetCurrentUserWorkspaceMemberships,
  FetchUserWorkspaceMemberships,
} from 'iam/WorkspaceMembershipStore'
import { CancellationToken } from 'infra/cancellation'

export type ScopeChangeRequestStatusFilterOption =
  | 'all'
  | 'reviewing'
  | 'awaiting'
  | 'accepted'
  | 'rejected'

export interface ScopeChangeRequestStatusFilter {
  count?: number
  type: ScopeChangeRequestStatusFilterOption
}

const ScopeChangeRequestStatusFilter = {
  none: {
    count: 0,
    type: 'all',
  } as ScopeChangeRequestStatusFilter,
  pending: {
    count: 0,
    type: 'reviewing',
  } as ScopeChangeRequestStatusFilter,
}

export type ScopeChangeRequestFilter = {
  workspaceIds: Id[]
  status: ScopeChangeRequestStatusFilter
}

/**
 * Field sort.
 */
export interface ScopeChangeRequestSort {
  field: SearchableScopeChangeRequestSortField
  direction: TableSortOrder
}

export type ScopeChangeRequestSearchFilter = {
  workspaceIds: Id[]
  statuses: ScopeChangeRequestStatus[]
  sort: ScopeChangeRequestSort
}

export const ScopeChangeRequestSearchFilter = {
  /**
   * An empty search filter.
   */
  none: {
    workspaceIds: [],
    sort: { field: 'create_time', direction: 'asc' },
    statuses: [],
  } as ScopeChangeRequestSearchFilter,

  /**
   * Whether the specified search filter is considered empty.
   */
  isNone: (searchFilter: ScopeChangeRequestSearchFilter) =>
    ScopeChangeRequestSearchFilter.equals(
      omit(ScopeChangeRequestSearchFilter.none, 'sort'),
      omit(searchFilter, 'sort'),
    ),

  /**
   * Whether 2 search filters are considered equal.
   */
  equals: (
    left: Partial<ScopeChangeRequestSearchFilter>,
    right: Partial<ScopeChangeRequestSearchFilter>,
  ) => areDeepEqual(left, right),
}

/**
 * Search result.
 */
export interface ScopeChangeRequestSearchResult {
  /**
   * The search filter that was used.
   */
  searchFilter: ScopeChangeRequestSearchFilter
  /**
   * Whether any search filters were applied.
   */
  filtersApplied: boolean
  /**
   * The current list of jobs contained in the searcher.
   */
  readonly scopeChangeRequests: readonly ScopeChangeRequest[]
}

/**
 * Sort direction.
 */
export const enum SortDirection {
  Ascending = 'asc',
  Descending = 'desc',
  None = 'none',
}

export default class ScopeChangeRequestsSearchViewModel {
  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  filters = new FiltersViewModel<ScopeChangeRequestFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      workspaceIds: {
        title: 'Workspaces',
        implicit: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(
              this.getWorkspacesWithScopeChangeRequests(),
            ),
            true,
          ),
      },
      status: {
        icon: 'pending',
        title: 'Quick Select',
        implicit: true,
        factory: (key, title) =>
          new SingleSelectFilter(
            title,
            key,
            ScopeChangeRequestStatusFilter.pending,
            this.getStatusList.bind(this),
          ),
      },
    },
  })

  /**
   * Sorting.
   */
  @observable sorting: ScopeChangeRequestSort = {
    field: 'create_time',
    direction: 'desc',
  }

  /**
   * The search text.
   */
  @observable searchText = ''

  /**
   * Backing field for the last search.
   */
  @observable.ref private _lastSearch: ScopeChangeRequestSearchResult = {
    scopeChangeRequests: [],
    searchFilter: ScopeChangeRequestSearchFilter.none,
    filtersApplied: false,
  }

  /**
   * Scope Change Requests scroller.
   */
  scroller = new InfiniteScrollProvider<
    ScopeChangeRequest,
    ScopeChangeRequestSearchFilter
  >({
    limit: 20,
    // We trigger the initial fetch ourselves. If this was false,
    // we would see 2 initial fetches.
    startFetchTaskAsPending: true,
    fetchItems: (paging, _, filter) =>
      this._fetchScopeChangeRequests(
        this.toFindScopeChangeRequestQuery(paging, filter),
      ),
  })

  /**
   * Search filter changes.
   */
  private searches$ = combineLatest([
    // ...with the remaining filter changes
    this.filters.appliedFilters$,
    // ...and the sort
    toStream(() => this.sorting, true),
  ]).pipe(
    map(
      ([appliedFilters, sort]): ScopeChangeRequestSearchFilter => ({
        workspaceIds: appliedFilters.workspaceIds || [],
        statuses: toJobScopeChangeRequestStatus(appliedFilters.status?.type),
        sort,
      }),
    ),
    // Ignore emissions with the same search filter.
    distinctUntilChanged<ScopeChangeRequestSearchFilter>(
      ScopeChangeRequestSearchFilter.equals,
    ),
    // Start a search, return the filter that was used.
    switchMap(
      (query): Observable<ScopeChangeRequestSearchResult> =>
        withCancellation(async (ct) => this._searchItems(query, ct)),
    ),
    switchMap(async (result) => {
      await this.workspaceFilter.loadOptions()
      await this.statusFilter.loadOptions()
      return result
    }),
    // Make sure to only subscribe once.
    shareLatest(),
  )

  constructor(
    private scopeChangeRequestStore: ScopeChangeRequestStore,
    private fetchWorkspaceMemberships: FetchUserWorkspaceMemberships,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private fetchJobs: FetchJobs,
    private filterStorage?: FilterStorage,
  ) {
    makeObservable(this)
  }

  /**
   * The internal scope change requests list ran through the transformer.
   */
  @computed
  get scopeChangeRequests() {
    return identity(uniq(this.scroller.items))
  }

  @computed
  get workspaceFilter() {
    return this.filters.getFilter('workspaceIds') as SelectFilter<string, Id>
  }

  @computed
  get statusFilter() {
    return this.filters.getFilter('status') as SingleSelectFilter<
      string,
      ScopeChangeRequestStatusFilter
    >
  }

  /**
   * Scope change requests filtered by status
   */
  @computed
  get requestsByStatus() {
    return this.getScopeChangeRequestsByStatusCount(
      this.statusFilter.value.type,
    )
  }

  /**
   * Scope change requests filtered by short id and details
   */
  @computed
  get filteredItems() {
    if (!this.searchText) {
      return this.scroller.items
    }

    const text = this.searchText.toLowerCase()
    return this.scroller.items.filter(
      (cr) =>
        cr.job.id?.toLowerCase()?.includes(text) ||
        cr.job.shortId.toLowerCase().includes(text) ||
        cr.job.displayName?.toLowerCase()?.includes(text) ||
        cr.workspace.name.toLocaleLowerCase().includes(text),
    )
  }

  async _searchItems(
    query: ScopeChangeRequestSearchFilter,
    ct?: CancellationToken,
  ) {
    await this.scroller.fetch(query)
    // Keep loading more until we get something back that isn't being filtered out
    // on the client. If we don't do this here, we potentially display an empty state
    // which does not trigger infinite scroll.
    while (this.scopeChangeRequests.length === 0 && !this.scroller.isDone) {
      if (ct) {
        ct.throwIfCancelled()
      }
      await this.scroller.more()
    }

    return {
      searchFilter: query,
      filtersApplied: !ScopeChangeRequestSearchFilter.isNone(query),
      scopeChangeRequests: this.scopeChangeRequests,
    }
  }

  /**
   * Refresh status filter
   * @returns
   */
  async refreshStatusFilter() {
    await this._searchItems(this._lastSearch.searchFilter)
    return this.statusFilter.loadOptions()
  }

  /**
   * Status list for status filter
   * @returns
   */
  async getStatusList(): Promise<FilterItem<ScopeChangeRequestStatusFilter>[]> {
    await this.scopeChangeRequestStore.fetchScopeChangeRequestStatusAggregations(
      this.workspaceFilter.value.map(Id.assert),
    )

    return [
      {
        title: 'Total Requests',
        value: {
          type: 'all',
          count: this.getScopeChangeRequestsByStatusCount('all'),
        },
      },
      {
        title: 'Pending review',
        value: {
          type: 'reviewing',
          count: this.getScopeChangeRequestsByStatusCount('reviewing'),
        },
      },
      {
        title: 'Awaiting response',
        value: {
          type: 'awaiting',
          count: this.getScopeChangeRequestsByStatusCount('awaiting'),
        },
      },
      {
        title: 'Update accepted',
        value: {
          type: 'accepted',
          count: this.getScopeChangeRequestsByStatusCount('accepted'),
        },
      },
      {
        title: 'Update rejected',
        value: {
          type: 'rejected',
          count: this.getScopeChangeRequestsByStatusCount('rejected'),
        },
      },
    ]
  }

  /**
   * Sets the scope change request sort.
   *
   * @param sortMethod
   */
  @action.bound
  setSortMethod(sortMethod: ScopeChangeRequestSort) {
    this.sorting = sortMethod
  }

  /**
   * Sets the scope change request sort.
   *
   * @param direction
   */
  @action.bound
  setSortDirection(direction: TableSortOrder) {
    // If we have no direction remove any sorting.
    if (direction === 'none') {
      this.setSortMethod({ field: 'create_time', direction: 'none' })
      return
    }

    this.setSortMethod({
      direction,
      field: this.sorting.field,
    })
  }

  /**
   * Sets the scope change request sort.
   *
   * @param field
   */
  @action.bound
  setSortField(field: SearchableScopeChangeRequestSortField) {
    this.setSortMethod({
      field,
      direction: this.sorting.direction,
    })
  }

  /**
   * Sets the search text.
   *
   * @param value
   */
  @action.bound
  setSearchText(value: string) {
    this.searchText = value
  }

  /**
   * Scope change requests filtered by status
   * @param status
   * @returns
   */
  getScopeChangeRequestsByStatusCount(
    status: ScopeChangeRequestStatusFilterOption,
  ) {
    if (status === 'all') {
      return this.scopeChangeRequestStore.scopeChangeRequestByStatus
        .slice()
        .reduce((acc, scr) => acc + scr.total, 0)
    }
    return this.scopeChangeRequestStore.scopeChangeRequestByStatus
      .filter((cr) => toJobScopeChangeRequestStatus(status).includes(cr.status))
      .reduce((acc, src) => acc + src.total, 0)
  }

  /**
   * Workspaces with scope change requests
   * @returns
   */
  getWorkspacesWithScopeChangeRequests(): FilterItem<Id>[] {
    return this.getWorkspaceMemberships()
      .filter((mb) =>
        mb.member.hasSomePermissions('ADMIN_JOB_SCOPE_CHANGE_MANAGE'),
      )
      .map((mbs) => {
        return { title: mbs.workspace.name, value: mbs.workspace.id }
      })
  }

  toFindScopeChangeRequestQuery(
    paging: CursorPageQuery,
    filter?: ScopeChangeRequestSearchFilter,
  ): FindScopeChangeRequest {
    return {
      workspaceIds: filter?.workspaceIds.map(Id.assert),
      filter: new ScopeChangeRequestQueryBuilder()
        .withStatuses(filter?.statuses)
        .build(),
      orderBy: `${filter?.sort.field.toString()} ${
        filter?.sort.direction !== 'none' ? filter?.sort.direction : ''
      }`,
      pageSize: paging.limit,
      pageToken: paging.after || undefined,
    }
  }

  async _fetchScopeChangeRequests(query: FindScopeChangeRequest) {
    const result =
      await this.scopeChangeRequestStore.searchScopeChangeRequests(query)

    await this.fetchJobs(result.data.map((cr) => Id.assert(cr.job.id)))

    return result
  }

  activate = task.resolved(async () => {
    this.filters.activate()
    this.subscription = this.searches$
      .pipe(
        // Can't use fromStream because it will subscribe immediately,
        // which we don't want.
        tapAction((search) => (this._lastSearch = search)),
      )
      .subscribe()
    await this.fetchWorkspaceMemberships()
  })

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.filters.deactivate()
    this.subscription.unsubscribe()
  }
}

function toJobScopeChangeRequestStatus(
  status?: ScopeChangeRequestStatusFilterOption,
) {
  if (!status) {
    return []
  }

  return match(status, {
    accepted: () => [ScopeChangeRequestStatus.Accepted],
    rejected: () => [
      ScopeChangeRequestStatus.Rejected,
      ScopeChangeRequestStatus.ApplyFailed,
    ],
    reviewing: () => [
      ScopeChangeRequestStatus.PendingAdminApproval,
      ScopeChangeRequestStatus.PendingFinalization,
    ],
    awaiting: () => [ScopeChangeRequestStatus.PendingClientApproval],
    all: () => [],
  })
}
